import type {
  ReflectionDefinition,
  ReflectionQuestion,
} from "./puzzlePackage";
import { seededRandom } from "./core/types";

export type ReflectionAnswerValue = string | string[];

export interface ReflectionSessionQuestion {
  question: ReflectionQuestion;
  choices?: Array<{ id: string; text: string; correct: boolean }>;
  variables?: Record<string, number>;
  prompt: string;
}

export interface ReflectionSession {
  questions: ReflectionSessionQuestion[];
}

export interface ReflectionScore {
  earnedPoints: number;
  totalPoints: number;
  percent: number;
  details: Array<{
    questionId: string;
    correct: boolean;
    expected?: number | string[];
  }>;
}

export function createReflectionSession(
  definition: ReflectionDefinition,
  seed: number,
): ReflectionSession {
  if (definition.mode !== "quiz") return { questions: [] };

  const random = seededRandom(seed);
  const questions = definition.shuffleQuestions
    ? shuffle(definition.questions, random)
    : [...definition.questions];
  const selected = questions.slice(0, definition.drawCount ?? questions.length);

  return {
    questions: selected.map((question) => {
      if (question.type === "multiple-choice") {
        return {
          question,
          prompt: question.prompt,
          choices: definition.shuffleChoices
            ? shuffle(question.choices, random)
            : [...question.choices],
        };
      }

      const variables = Object.fromEntries(
        question.variables.map((variable) => [
          variable.key,
          generateVariableValue(variable, random),
        ]),
      );

      return {
        question,
        variables,
        prompt: renderPrompt(question.prompt, variables),
      };
    }),
  };
}

export function scoreReflectionAnswers(
  session: ReflectionSession,
  answers: Record<string, ReflectionAnswerValue>,
): ReflectionScore {
  let earnedPoints = 0;
  let totalPoints = 0;
  const details: ReflectionScore["details"] = [];

  for (const item of session.questions) {
    const { question } = item;
    totalPoints += question.points;

    if (question.type === "multiple-choice") {
      const answer = answers[question.id];
      const selectedIds = Array.isArray(answer)
        ? answer
        : typeof answer === "string" && answer
          ? [answer]
          : [];
      const expected = question.choices
        .filter((choice) => choice.correct)
        .map((choice) => choice.id)
        .sort();
      const selected = [...selectedIds].sort();
      const correct =
        selected.length === expected.length &&
        selected.every((choiceId, index) => choiceId === expected[index]);
      if (correct) earnedPoints += question.points;
      details.push({ questionId: question.id, correct, expected });
      continue;
    }

    const answer = answers[question.id];
    const numericAnswer =
      typeof answer === "string" ? Number.parseFloat(answer) : Number.NaN;
    const expected = evaluateExpression(
      question.answerExpression,
      item.variables ?? {},
    );
    const correct =
      Number.isFinite(numericAnswer) &&
      Math.abs(numericAnswer - expected) <= question.tolerance;
    if (correct) earnedPoints += question.points;
    details.push({ questionId: question.id, correct, expected });
  }

  return {
    earnedPoints,
    totalPoints,
    percent: totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0,
    details,
  };
}

export function evaluateExpression(
  expression: string,
  variables: Record<string, number>,
) {
  const parser = new ExpressionParser(expression, variables);
  return parser.parse();
}

function shuffle<T>(items: T[], random: () => number) {
  const copy = [...items];
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
  }
  return copy;
}

function generateVariableValue(
  variable: {
    min: number;
    max: number;
    step: number;
    decimals: number;
  },
  random: () => number,
) {
  const steps = Math.max(0, Math.floor((variable.max - variable.min) / variable.step));
  const value = variable.min + Math.floor(random() * (steps + 1)) * variable.step;
  return Number(value.toFixed(variable.decimals));
}

function renderPrompt(prompt: string, variables: Record<string, number>) {
  return Object.entries(variables).reduce(
    (result, [key, value]) =>
      result
        .replaceAll(`{{${key}}}`, String(value))
        .replaceAll(`{${key}}`, String(value)),
    prompt,
  );
}

class ExpressionParser {
  private index = 0;

  constructor(
    private readonly source: string,
    private readonly variables: Record<string, number>,
  ) {}

  parse() {
    const value = this.parseExpression();
    this.skipWhitespace();
    if (this.index < this.source.length) {
      throw new Error(`Unexpected token "${this.source[this.index]}".`);
    }
    return value;
  }

  private parseExpression(): number {
    let value: number = this.parseTerm();
    while (true) {
      this.skipWhitespace();
      if (this.consume("+")) value += this.parseTerm();
      else if (this.consume("-")) value -= this.parseTerm();
      else return value;
    }
  }

  private parseTerm(): number {
    let value: number = this.parsePower();
    while (true) {
      this.skipWhitespace();
      if (this.consume("*")) value *= this.parsePower();
      else if (this.consume("/")) value /= this.parsePower();
      else return value;
    }
  }

  private parsePower(): number {
    let value: number = this.parseUnary();
    this.skipWhitespace();
    if (this.consume("^")) {
      value = value ** this.parsePower();
    }
    return value;
  }

  private parseUnary(): number {
    this.skipWhitespace();
    if (this.consume("-")) return -this.parseUnary();
    if (this.consume("+")) return this.parseUnary();
    return this.parsePrimary();
  }

  private parsePrimary(): number {
    this.skipWhitespace();
    if (this.consume("(")) {
      const value: number = this.parseExpression();
      if (!this.consume(")")) throw new Error("Expected closing parenthesis.");
      return value;
    }

    const number = this.readNumber();
    if (number !== null) return number;

    const identifier = this.readIdentifier();
    if (identifier) {
      const value = this.variables[identifier];
      if (typeof value !== "number") {
        throw new Error(`Unknown variable "${identifier}".`);
      }
      return value;
    }

    throw new Error("Expected a number, variable, or expression.");
  }

  private readNumber() {
    const match = /^[0-9]*\.?[0-9]+/.exec(this.source.slice(this.index));
    if (!match) return null;
    this.index += match[0].length;
    return Number(match[0]);
  }

  private readIdentifier() {
    const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(this.source.slice(this.index));
    if (!match) return null;
    this.index += match[0].length;
    return match[0];
  }

  private consume(token: string) {
    this.skipWhitespace();
    if (!this.source.startsWith(token, this.index)) return false;
    this.index += token.length;
    return true;
  }

  private skipWhitespace() {
    while (/\s/.test(this.source[this.index] ?? "")) this.index += 1;
  }
}
